import axios from "axios";

const RAW_BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").trim();
const BACKEND_URL = RAW_BACKEND_URL.replace(/\/+$/, "");
export const API_BASE = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// ── Access token (Bearer fallback) ──────────────────────────
// The backend sets an HttpOnly `access_token` cookie with
// `Secure=True; SameSite=None`. Browsers refuse to send that cookie
// on plain-HTTP origins (e.g. a local self-hosted copy on http://...)
// so every API call returns 401. We persist the token in localStorage
// at login time and attach it as `Authorization: Bearer <token>` on
// every outgoing request. On HTTPS the cookie keeps working; on HTTP
// the Bearer header takes over. Both flows hit the same JWT verifier.
const TOKEN_KEY = "isaloon_access_token";
export const setAccessToken = (token) => {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch { /* noop */ }
};
export const getAccessToken = () => {
  try { return localStorage.getItem(TOKEN_KEY) || ""; }
  catch { return ""; }
};

// ── Branch context ──────────────────────────────────────────
// Frontend persists active branch_id in localStorage and sends
// it on every request via X-Branch-Id header. Backend honours it
// to scope writes (and later, reads).
export const getActiveBranchId = () => {
  try { return localStorage.getItem("active_branch_id") || ""; }
  catch { return ""; }
};

export const setActiveBranchId = (id) => {
  try {
    if (id) localStorage.setItem("active_branch_id", id);
    else localStorage.removeItem("active_branch_id");
  } catch { /* noop */ }
};

/** Authenticated file download (Excel/PDF/CSV) via fetch + blob. */
export async function downloadFile(url, filename) {
  const headers = {};
  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const bid = getActiveBranchId();
  if (bid) headers["X-Branch-Id"] = bid;
  const r = await fetch(url, { credentials: "include", headers });
  if (!r.ok) throw new Error(`Download failed (${r.status})`);
  const blob = await r.blob();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 0);
}

api.interceptors.request.use((config) => {
  // Never leak the branch context onto public, unauthenticated endpoints.
  const url = config.url || "";
  if (url.includes("/api/public/") || url.startsWith("/public/")) {
    if (config.headers && config.headers["X-Branch-Id"]) {
      delete config.headers["X-Branch-Id"];
    }
    return config;
  }
  // Attach Bearer token from localStorage. Works on HTTP (where the
  // Secure cookie is dropped by the browser) and is harmless on HTTPS
  // because the backend accepts either credential.
  const token = getAccessToken();
  if (token) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  const bid = getActiveBranchId();
  if (bid) {
    config.headers = config.headers || {};
    config.headers["X-Branch-Id"] = bid;
  }
  return config;
});

// ── Plan-gated 403 → resolved-empty rewrite ─────────────────
// The plan-page gate on the backend returns 403 with a structured detail
// payload. These 403s come from auxiliary calls inside `Promise.all`
// batches where the page IS visible but one sibling endpoint isn't on
// the tenant's plan (e.g. POS opens but /api/accounts is denied because
// the Basic plan doesn't include it).
//
// Letting these bubble up creates a noisy uncaught rejection that the
// React dev overlay surfaces, even though every operator-facing concern
// is already handled by:
//   • ModuleGuard (reads from `/api/me/subscription`, not from errors)
//   • The sidebar filter (hides links to denied pages entirely)
//   • The explicit toasts on mutating actions (POST/PUT/DELETE)
//
// So for READ-style methods (GET/HEAD) we silently resolve the call with
// an empty payload — the page renders without the optional data. For
// WRITE methods we still reject so the toast in the catch block can show
// the bilingual upgrade message. This is the dividing line between
// "fail soft" (incidental fetches) and "fail loud" (deliberate actions).
api.interceptors.response.use(
  (r) => r,
  (err) => {
    const code = err?.response?.data?.detail?.code;
    const isPlanGated =
      err?.response?.status === 403 &&
      (code === "plan_page_not_included" || code === "plan_quota_exceeded");
    if (isPlanGated) {
      err.__planGated = true;
      // eslint-disable-next-line no-console
      console.warn(`[plan-gate] ${code} on ${err?.config?.url}`);
      const method = (err?.config?.method || "get").toLowerCase();
      if (method === "get" || method === "head") {
        // Resolve as an empty success so Promise.all never rejects on
        // auxiliary reads from plan-denied endpoints.
        return Promise.resolve({
          data: [],
          status: 204,
          statusText: "No Content (plan-gated)",
          headers: {},
          config: err.config,
          __planGated: true,
        });
      }
    }
    return Promise.reject(err);
  },
);
